import { z } from 'zod';
/** At most this many cards under one message. */
export const MAX_JIRA_CARDS = 5;
export const JiraTransitionRequest = z.object({ transitionId: z.string().min(1).max(32) });
/** `null` unassigns. */
export const JiraAssignRequest = z.object({ accountId: z.string().min(1).max(128).nullable() });
/** Plain text; `<@userId>` of someone with Jira connected becomes a real Jira mention. */
export const JiraCommentRequest = z.object({ text: z.string().trim().min(1).max(10_000) });
/** What a channel's Jira feed can carry (docs/plans/2026-11-04-jira-design.md §3). */
export const JIRA_FEED_KINDS = ['created', 'status', 'assigned', 'commented'];
export const CreateJiraSubscriptionRequest = z.object({
    projectKey: z.string().regex(/^[A-Z][A-Z0-9_]{0,19}$/),
    kinds: z.array(z.enum(JIRA_FEED_KINDS)).min(1),
    issueTypes: z.array(z.string().trim().min(1).max(100)).min(1).max(30).nullable().default(null),
});
/**
 * `POST /integrations/jira/issues` (docs/plans/2026-11-04-jira-design.md §4): a new issue, as the
 * person. From a message (`messageId`): its thread gets "NOV-130'u açtım"; from `/jira` in a channel
 * (`channelId`): the channel gets it. Jira's own refusal (a required field) comes back as a 400.
 */
export const CreateJiraIssueRequest = z.object({
    projectKey: z.string().regex(/^[A-Z][A-Z0-9_]{0,19}$/),
    issueType: z.string().trim().min(1).max(100),
    summary: z.string().trim().min(1).max(255),
    description: z.string().max(10_000).default(''),
    assignToMe: z.boolean().default(false),
    messageId: z.uuid().optional(),
    channelId: z.uuid().optional(),
});
/** Status colors (dark theme), from the "Benim işlerim" design: Jira only says a status's category, so each known status gets its own. */
export const JIRA_STATUS_COLORS = {
    gray: { dot: '#858A95', tint: 'rgba(133, 138, 149, 0.16)', line: 'rgba(133, 138, 149, 0.42)' },
    slate: { dot: '#B6BECB', tint: 'rgba(182, 190, 203, 0.14)', line: 'rgba(182, 190, 203, 0.40)' },
    blue: { dot: '#59AAF8', tint: 'rgba(89, 170, 248, 0.18)', line: 'rgba(89, 170, 248, 0.50)' },
    purple: { dot: '#B199F4', tint: 'rgba(177, 153, 244, 0.18)', line: 'rgba(177, 153, 244, 0.50)' },
    yellow: { dot: '#EEBC4A', tint: 'rgba(238, 188, 74, 0.15)', line: 'rgba(238, 188, 74, 0.45)' },
    green: { dot: '#68CA80', tint: 'rgba(104, 202, 128, 0.16)', line: 'rgba(104, 202, 128, 0.45)' },
};
/** Yapılacak gri, Açık açık gri, Devam ediyor mavi, Test mor, Bilgi bekliyor sarı, Tamam yeşil; others by their category. */
export function jiraStatusKey(status) {
    const name = status.name.toLocaleLowerCase('tr');
    if (/test|qa|review|inceleme/.test(name))
        return 'purple';
    if (/bilgi|bekl|waiting|blocked|engel/.test(name))
        return 'yellow';
    if (status.category === 'done')
        return 'green';
    if (status.category === 'doing')
        return 'blue';
    return /açık|open/.test(name) ? 'slate' : 'gray';
}
export function jiraStatusColor(status) {
    return JIRA_STATUS_COLORS[jiraStatusKey(status)];
}
/** Groups in "Benim işlerim": what is moving first (Devam ediyor → Test → Bilgi bekliyor → Açık → Yapılacak). */
export const JIRA_GROUP_ORDER = ['blue', 'purple', 'yellow', 'slate', 'gray', 'green'];
/** Statuses to move to, in workflow order (Yapılacak → Açık → Devam ediyor → Bilgi bekliyor → Test → Tamam). */
export const JIRA_FLOW_ORDER = ['gray', 'slate', 'blue', 'yellow', 'purple', 'green'];
/**
 * What a summary carries by the team's habit (docs/plans/2026-11-04-jira-design.md §7):
 * "Admin FE: Başlık (NOV-4333)" is the module, the title and the issue it belongs to. Without a
 * module prefix, the epic stands in. A sprint prefix ("V70: ") is dropped.
 */
export function jiraTitleParts(summary, epic = null) {
    let title = summary.trim().replace(/^V\d+:\s*/, '');
    let ref = null;
    const tail = /\s*\(([A-Z][A-Z0-9_]+-\d+)\)\s*$/.exec(title);
    if (tail) {
        ref = tail[1];
        title = title.slice(0, tail.index);
    }
    let module = null;
    const head = /^([\p{L}\d][\p{L}\d &+/.-]{1,28}?):\s+/u.exec(title);
    if (head) {
        module = head[1].trim();
        title = title.slice(head[0].length);
        title = title ? title[0].toLocaleUpperCase('tr') + title.slice(1) : summary;
    }
    return { module: module ?? epic, title: title || summary, ref };
}
/** Admin blue, Company green, both teal; any other module (an epic) quiet. */
export function jiraModuleColor(module) {
    const name = module.toLocaleLowerCase('tr');
    const admin = name.includes('admin');
    const company = name.includes('company');
    return admin && company ? '#64D1D7' : admin ? '#8CC3FC' : company ? '#82D2A8' : '#A9B1BD';
}
/** Bars out of three, as the design draws priority: Highest/High 3, Medium 2, Low/Lowest 1. */
export function jiraPriorityLevel(name) {
    if (!name)
        return 0;
    if (/highest|critical|blocker|en yüksek|kritik|high|yüksek/i.test(name))
        return 3;
    if (/low|düşük/i.test(name))
        return 1;
    return 2;
}
export const JIRA_PRIORITY_COLOR = '#E8AA4E';
/** The latest this many comments come with an issue's details; Jira has the rest. */
export const JIRA_DETAIL_COMMENTS = 50;
//# sourceMappingURL=jira.js.map